import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, throwError } from 'rxjs';
import { ToastrType } from '../constants/ConstantVariables';
import { AuthService } from 'src/app/services/auth/auth.service';
import { UtilityService } from 'src/app/services/utility/utility.service';

// EN-18175 - the message TerminatedEmployeeAccessFilter returns with its 403. Matching on it keeps other
// 403s (e.g. the MFA claim check) out of this handling.
export const FEATURE_NOT_AVAILABLE_MESSAGE = 'This feature is not available for your account.';
// EN-33066 - returned with 403 on every API once a terminated employee's access months have passed.
export const PORTAL_ACCESS_ENDED_MESSAGE = 'Your Employee Portal access has ended.';
// Long enough to read the message before LogOut leaves for the SSO login page.
const ACCESS_ENDED_LOGOUT_DELAY_MS = 3000;
// Every call made at that moment answers the same 403; the message and logout happen once.
let accessEndedLogoutPending = false;

/**
 * EN-18175 - an employee terminated while signed in hits a 403 on their next action (AC4/AC5). Tell them,
 * reload their access so the menu shrinks, and move them to their landing page (Summary, EN-33071).
 */
export const terminatedAccessInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const utilityService = inject(UtilityService);
  const translate = inject(TranslateService);

  return next(req).pipe(
    catchError((error) => {
      // EN-33066 - the access months are over: nothing in the portal is open any more, so sign them out.
      if (
        error instanceof HttpErrorResponse &&
        error.status === 403 &&
        error.error?.message === PORTAL_ACCESS_ENDED_MESSAGE
      ) {
        if (!accessEndedLogoutPending) {
          accessEndedLogoutPending = true;
          utilityService.Toastr(ToastrType.Error, translate.instant(PORTAL_ACCESS_ENDED_MESSAGE));
          setTimeout(() => {
            accessEndedLogoutPending = false;
            authService.LogOut();
          }, ACCESS_ENDED_LOGOUT_DELAY_MS);
        }
        return throwError(() => error);
      }

      if (
        error instanceof HttpErrorResponse &&
        error.status === 403 &&
        error.error?.message === FEATURE_NOT_AVAILABLE_MESSAGE
      ) {
        utilityService.Toastr(ToastrType.Error, translate.instant(FEATURE_NOT_AVAILABLE_MESSAGE));
        authService
          .getEmployeeAccessModules()
          .then(() => {
            if (authService.isTerminatedRestricted) {
              router.navigate([authService.getLandingRoute()]);
            }
          })
          .catch(() => {});
      }
      return throwError(() => error);
    })
  );
};
